/** @jsxImportSource @emotion/react */
"use client";

import type { Interpolation } from "@emotion/react";
import { useTranslations } from "@i18n/react";
import { Link } from "@packages/framework";
import type { CommunitiesAdminMostPopular } from "@packages/sdk";

import type { ThemeType } from "../../../../../../global/emotion";
import { numericPercentages, spacing } from "../../../../../../global/emotion";
import { useCarousel } from "../../../../../hooks/useCarousel";
import { cssMediaQueryMinWidthTablet } from "../../../../../lib/constants/emotionMediaQueries.constants";
import { Text } from "../../../../_base/_core/Text/Text.emotion";
import { ContentMedium } from "../../../../_base/content/content/ContentMedium/ContentMedium.emotion";
import { VideoIcon } from "../../../../_base/icons/Icon/GeneralIcons";
import { Carousel } from "../../../layout/Carousel/Carousel.emotion";

const LOADING_SKELETONS_TOTAL = 8;

const carouselStyle: Interpolation = {
  maxWidth: numericPercentages[100],
  [cssMediaQueryMinWidthTablet]: {
    maxWidth: `calc(100vw - ${spacing.sidebar})`,
  },
  paddingBottom: spacing.m,
  paddingTop: spacing.m,
};

const messageStyle: Interpolation<ThemeType> = (theme) => ({
  color: theme.semanticColors.neutralsHigh,
  paddingBottom: spacing.xl,
  paddingLeft: spacing.l,
  paddingRight: spacing.m,
  paddingTop: spacing.xs,
  [cssMediaQueryMinWidthTablet]: {
    paddingLeft: spacing.xl,
    paddingRight: spacing.l,
  },
});

export type CarouselCommunityMostPopularProps = {
  data: CommunitiesAdminMostPopular;
  error?: boolean;
  id: number;
  loading?: boolean;
};

export const CarouselCommunityMostPopular = ({
  data,
  error = false,
  id,
  loading = false,
}: CarouselCommunityMostPopularProps) => {
  const t = useTranslations();
  const carouselApi = useCarousel({
    pageOffset: [
      [24, 32],
      [24, 24],
    ],
    size: [2, 2, 3, 4, 5, 5],
  });

  if (error) {
    return (
      <Text css={messageStyle} size="xl" type="detail">
        {t.has("admin_dash_most_popular_error")
          ? t("admin_dash_most_popular_error")
          : "Most popular content could not be loaded."}
      </Text>
    );
  }

  if (!loading && !data?.results?.length) {
    return (
      <Text css={messageStyle} size="xl" type="detail">
        {t.has("admin_dash_most_popular_empty")
          ? t("admin_dash_most_popular_empty")
          : "No content has been played yet."}
      </Text>
    );
  }

  return (
    <Carousel carouselApi={carouselApi} containerCSS={carouselStyle}>
      {loading
        ? [...Array(LOADING_SKELETONS_TOTAL)].map((index) => (
            <ContentMedium key={index} loading />
          ))
        : data?.results?.map((mostPopular, index) => {
            const title = mostPopular.name ?? mostPopular.title;
            const description =
              mostPopular.short_desc ??
              mostPopular.label_desc ??
              mostPopular.description;
            const metadata =
              mostPopular?.lengths ?? mostPopular.sessions ?? null;
            const href = `/admin/${id}/dashboard/${
              mostPopular.sessions
                ? `collections/${mostPopular.id}`
                : `prayers/${mostPopular.id}`
            }`;
            return (
              <ContentMedium
                asChild
                description={description}
                imgSrc={mostPopular.images}
                metadata={metadata}
                metadataInlineIcon={
                  mostPopular.has_video ? <VideoIcon.Filled /> : null
                }
                rank={index + 1}
                title={title}
              >
                <Link href={href} />
              </ContentMedium>
            );
          })}
    </Carousel>
  );
};
